import { useEffect, useRef, type KeyboardEvent } from 'react';
import { User, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

type EmployeeViewTab = 'details' | 'calendar';

interface EmployeeViewTabsProps {
  activeTab: EmployeeViewTab;
  onTabChange: (tab: EmployeeViewTab) => void;
}

const tabs: { key: EmployeeViewTab; label: string; icon: typeof User }[] = [
  { key: 'details', label: 'Details', icon: User },
  { key: 'calendar', label: 'Kalender', icon: Calendar },
];

// Konstante IDs — Caller (HRDashboard/ManagerDashboard) verwenden diese
// als id ihrer tabpanel-Container, damit aria-controls auflöst.
export const TAB_BUTTON_ID: Record<EmployeeViewTab, string> = {
  details: 'employee-view-tab-details',
  calendar: 'employee-view-tab-calendar',
};
export const TAB_PANEL_ID: Record<EmployeeViewTab, string> = {
  details: 'employee-view-panel-details',
  calendar: 'employee-view-panel-calendar',
};

export function EmployeeViewTabs({ activeTab, onTabChange }: EmployeeViewTabsProps) {
  const buttonRefs = useRef<Record<EmployeeViewTab, HTMLButtonElement | null>>({
    details: null,
    calendar: null,
  });
  // Tracked, ob Focus durch Keyboard-Nav verschoben wurde — sonst stiehlt
  // der Effect Focus z. B. beim initialen Mount.
  const shouldFocusActiveRef = useRef(false);

  useEffect(() => {
    if (!shouldFocusActiveRef.current) return;
    shouldFocusActiveRef.current = false;
    buttonRefs.current[activeTab]?.focus();
  }, [activeTab]);

  const handleKeyDown = (e: KeyboardEvent, currentKey: EmployeeViewTab) => {
    const currentIndex = tabs.findIndex((t) => t.key === currentKey);
    let nextIndex = currentIndex;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    e.preventDefault();
    shouldFocusActiveRef.current = true;
    onTabChange(tabs[nextIndex]!.key);
  };

  return (
    <div
      role="tablist"
      aria-label="Mitarbeiteransicht"
      className="inline-flex items-center rounded-lg border border-border bg-muted/30 p-0.5"
    >
      {tabs.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          ref={(el) => {
            buttonRefs.current[key] = el;
          }}
          id={TAB_BUTTON_ID[key]}
          role="tab"
          aria-selected={activeTab === key}
          aria-controls={TAB_PANEL_ID[key]}
          tabIndex={activeTab === key ? 0 : -1}
          onClick={() => onTabChange(key)}
          onKeyDown={(e) => handleKeyDown(e, key)}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            activeTab === key
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      ))}
    </div>
  );
}
